import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    CircularProgress, Box, Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import config from "../config";
import * as Sentry from "@sentry/react";
import { validateLoggedIn, getSession } from "../utils";
import NavBar from "../navbar";
import { formatUtc } from "../utc";

const PointTransactions = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [orderBy, setOrderBy] = useState("created_at");
    const [order, setOrder] = useState("desc");

    useEffect(() => {
        const validate = validateLoggedIn();
        if (validate) { setError(validate); return; }

        const fetchData = async () => {
            try {
                const response = await fetch(`${config.apiBaseUrl}/admin/point-transactions`, {
                    headers: {
                        "Content-Type": "application/json",
                        "user-authorization": getSession().token,
                    },
                });
                if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);
                setData(await response.json());
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            }
        };
        fetchData();
    }, []);

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(column);
    };

    const formatPrice = (cents) => cents !== null ? `$${(cents / 100).toFixed(2)}` : "—";

    const sortData = (rows, orderBy, order) => {
        return [...rows].sort((a, b) => {
            const valA = a[orderBy]; const valB = b[orderBy];
            if (valA === null || valA === undefined) return 1;
            if (valB === null || valB === undefined) return -1;
            if (typeof valA === "number" && typeof valB === "number") return order === "asc" ? valA - valB : valB - valA;
            if (!isNaN(Date.parse(valA)) && !isNaN(Date.parse(valB)))
                return order === "asc" ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
            return order === "asc" ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
        });
    };

    if (error) return <Box textAlign="center" p={2} color="red">{error}</Box>;
    if (!data) return <Box textAlign="center" p={2}><CircularProgress /></Box>;
    if (data.length === 0) return (<><NavBar /><Box textAlign="center" p={2}><Typography>No point transactions found</Typography></Box></>);

    const sorted = sortData(data, orderBy, order);

    return (
        <>
            <NavBar />
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" sx={{ mb: 3 }}>Point Transactions</Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {[
                                    { col: "user_email", label: "User" },
                                    { col: "amount", label: "Amount" },
                                    { col: "transaction_type", label: "Type" },
                                    { col: "balance_after", label: "Balance After" },
                                    { col: "price_cents", label: "Price" },
                                    { col: "created_at", label: "Date" },
                                ].map(({ col, label }) => (
                                    <TableCell key={col} onClick={() => handleSort(col)} style={{ cursor: "pointer" }}>
                                        {label}
                                        {orderBy === col ? (
                                            order === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                        ) : null}
                                    </TableCell>
                                ))}
                                <TableCell>Receipt</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.user_email}</TableCell>
                                    <TableCell>{row.amount.toLocaleString()}</TableCell>
                                    <TableCell>{row.transaction_type}</TableCell>
                                    <TableCell>{row.balance_after.toLocaleString()}</TableCell>
                                    <TableCell>{formatPrice(row.price_cents)}</TableCell>
                                    <TableCell>{formatUtc(row.created_at, "—")}</TableCell>
                                    <TableCell>
                                        {row.stripe_receipt_url ? (
                                            <a href={row.stripe_receipt_url} target="_blank" rel="noopener noreferrer">View</a>
                                        ) : "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};

export default PointTransactions;
